import React, { useMemo } from "react";
import { Provider } from "react-redux";
import configureStore from "../store/configure-store";
import { publicConfig } from "../util/public-config";
import { composeDataMethods } from "@lululemon/mwa-utils";

const __NEXT_REDUX_STORE__ = "__NEXT_REDUX_STORE__";

export function getOrCreateStore(initialState) {
  // Always make a new store if server, otherwise state is shared between requests
  if (!process.browser) {
    return configureStore(initialState);
  }

  // Create store if unavailable on the client and set it on the window object
  if (!window[__NEXT_REDUX_STORE__]) {
    window[__NEXT_REDUX_STORE__] = configureStore(initialState);

    // In local env, rewrite pages cache to include host for proxying purpose
    if (process.browser && process.env.NODE_ENV === "development") {
      const url = require("url");
      const pages = window[__NEXT_REDUX_STORE__].getState().rootReducer.pages;

      for (const key in pages) {
        const val = pages[key];
        delete pages[key];
        pages[`https://${publicConfig.host}${url.parse(key).path}`] = val;
      }
    }

    if (process.browser) {
      // Reset all page cache timestamps from SSR to now to ensure they aren't considered stale
      const pages = window[__NEXT_REDUX_STORE__].getState().rootReducer.pages;
      for (const key in pages) {
        const val = pages[key];
        val.timestamp = new Date().getTime();
      }
    }
  }

  return window[__NEXT_REDUX_STORE__];
}

const withReduxStore = (App) => {
  const AppWithRedux = (props) => {
    const reduxStore = useMemo(
      () => props.__reduxStore || getOrCreateStore(props.initialReduxState),
      []
    );

    return (
      <Provider store={reduxStore}>
        <App {...props} reduxStore={reduxStore} />
      </Provider>
    );
  };

  return composeDataMethods(App, {
    prepareInitialPropsContext(appContext) {
      const reduxStore = getOrCreateStore();

      if (!process.browser) {
        reduxStore.dispatch({ type: "SET_LANG" });
      } else if (appContext.federation) {
        reduxStore.dispatch({ type: "SET_LANG" });
      }

      // if we wanted to move it down to the page level
      const ctx = appContext.ctx || appContext;
      // Provide the store to getInitialProps of pages (global level)
      ctx.reduxStore = reduxStore;
    },

    getInitialProps(appContext) {
      // so it works at the _app level and page level
      // needed for module federation or composing on a page
      const ctx = appContext.ctx || appContext;
      return {
        __reduxStore: ctx.reduxStore,
      };
    },

    postProcessInitialProps({ __reduxStore: reduxStore, ...rest }) {
      const initialState = reduxStore.getState();

      return {
        ...rest,
        initialReduxState: initialState,
      };
    },
  })(AppWithRedux);
};

export default withReduxStore;
